import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopeParams } from "./useScopeParams";
import { planOfRecordService } from "@/services/planOfRecordService";
import { PLAN_OF_RECORD_DATASET_TYPE } from "@/views/PlanOfRecord/planOfRecordMapping";
import type {
  CreateMilestonePayload,
  UpdateMilestonePayload,
} from "@/views/PlanOfRecord/planOfRecord.types";

export function usePlanOfRecordMutations() {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();

  const invalidatePlanOfRecord = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [keyPrefix, , body] = query.queryKey as [string, string, unknown];
        if (
          typeof keyPrefix === "string" &&
          keyPrefix.includes(PLAN_OF_RECORD_DATASET_TYPE)
        ) {
          return true;
        }
        return (
          !!body &&
          typeof body === "object" &&
          (body as { dataset_type?: string }).dataset_type ===
            PLAN_OF_RECORD_DATASET_TYPE
        );
      },
    });
  }, [queryClient]);

  const createMilestoneMutation = useMutation({
    mutationFn: (payload: CreateMilestonePayload) =>
      planOfRecordService.createMilestone(id, payload),
    onSuccess: invalidatePlanOfRecord,
  });

  const updateMilestoneMutation = useMutation({
    mutationFn: ({
      milestoneId,
      payload,
    }: {
      milestoneId: string;
      payload: UpdateMilestonePayload;
    }) => planOfRecordService.updateMilestone(id, milestoneId, payload),
    onSuccess: invalidatePlanOfRecord,
  });

  const deleteMilestoneMutation = useMutation({
    mutationFn: ({
      milestoneId,
      rationale,
    }: {
      milestoneId: string;
      rationale?: string;
    }) => planOfRecordService.deleteMilestone(id, milestoneId, rationale),
    onSuccess: invalidatePlanOfRecord,
  });

  return {
    createMilestone: createMilestoneMutation.mutateAsync,
    updateMilestone: updateMilestoneMutation.mutateAsync,
    deleteMilestone: deleteMilestoneMutation.mutateAsync,
    isCreating: createMilestoneMutation.isPending,
    isUpdating: updateMilestoneMutation.isPending,
    isDeleting: deleteMilestoneMutation.isPending,
  };
}
